import * as React from 'react';
import TextInput from './TextInput';
import SearchSuggestions from './SearchSuggestions';
import { HatType } from './Hat';

export interface SearchInputProps {
  placeHolder?: string;
  name: string;
  value?: string;
  id: string;
  label: string;
  required?: boolean;
  focus?: boolean;
  hasError?: boolean;
  errors?: any;
  theme?: 'night' | 'day';
  maxLength?: number;
  onChange?: Function;
  onError?: Function;
  onBlur?: Function;
  onSearch?: Function;
  onClear?: Function;
  onSuggestion?: Function;
  onLeft?: Function;
  onRight?: Function;
  onSpace?: Function;
  autoSuggest?: boolean;
  suggestOn?: number;
  suggestionsList?: string[];
  maxSuggestions?: number;
  hat?: HatType[];
  disabled?: boolean;
  toolTip?: React.ReactNode;
  autocomplete?: string;
}

interface SearchInputState {
  inputValue: string;
  displaySearchSuggestions: boolean;
  selectedSuggestionIndex: number;
  searchValue: string;
}

export default function SearchInput(props: SearchInputProps) {
  const { suggestOn = 3, maxSuggestions = 5, theme = 'day' } = props;

  const [state, setState] = React.useState<SearchInputState>({
    searchValue: props.value || '',
    displaySearchSuggestions: false,
    selectedSuggestionIndex: -1,
    inputValue: '',
  });
  const suggestions =
    props.suggestionsList && props.suggestionsList.slice(0, maxSuggestions);

  return (
    <TextInput
      placeHolder={props.placeHolder || 'Search'}
      inputMode="search"
      name={props.name}
      value={state.searchValue}
      id={props.id}
      label={props.label}
      required={props.required}
      focus={props.focus}
      hasError={props.hasError}
      errors={props.errors}
      theme={props.theme}
      maxLength={props.maxLength}
      type="search"
      hideLabel
      hat={props.hat}
      onError={props.onError}
      autocomplete={props.autocomplete}
      onBlur={(event: any) => {
        setState({
          ...state,
          displaySearchSuggestions: false,
        });
        if (props.onBlur !== undefined) {
          props.onBlur(event);
        }
      }}
      onChange={(event: any) => {
        let displaySearchSuggestions = false;
        if (props.autoSuggest && event.target.value.length >= suggestOn) {
          if (props.onSuggestion !== undefined) {
            props.onSuggestion(event.target.value);
          }
          displaySearchSuggestions = true;
        }
        setState({
          ...state,
          searchValue: event.target.value,
          displaySearchSuggestions,
          inputValue: event.target.value,
        });
        if (props.onChange !== undefined) {
          props.onChange({
            ...event,
            target: { ...event.target },
          });
        }
      }}
      onEsc={(inputValue: string) => {
        if (props.onClear !== undefined) {
          props.onClear(inputValue);
        }
        setState({
          ...state,
          searchValue: '',
          displaySearchSuggestions: false,
        });
      }}
      onEnter={(inputValue: string) => {
        if (props.onSearch !== undefined) {
          props.onSearch(inputValue.replace(/(^['"]+|['"]+$)/g, ''));
        }
        setState({
          ...state,
          searchValue: inputValue,
          displaySearchSuggestions: false,
        });
      }}
      onUp={() => {
        if (suggestions) {
          let { selectedSuggestionIndex } = state;
          selectedSuggestionIndex += -1;

          if (selectedSuggestionIndex > suggestions.length - 1) {
            selectedSuggestionIndex = -1;
          } else if (selectedSuggestionIndex < -1) {
            selectedSuggestionIndex = suggestions.length - 1;
          }

          const value =
            selectedSuggestionIndex > -1
              ? suggestions[selectedSuggestionIndex]
              : state.inputValue;
          if (selectedSuggestionIndex !== state.selectedSuggestionIndex) {
            setState({
              ...state,
              selectedSuggestionIndex,
              searchValue: value,
            });
          }
        }
      }}
      onDown={() => {
        if (suggestions) {
          let { selectedSuggestionIndex } = state;
          selectedSuggestionIndex += 1;

          if (selectedSuggestionIndex > suggestions.length - 1) {
            selectedSuggestionIndex = -1;
          } else if (selectedSuggestionIndex < -1) {
            selectedSuggestionIndex = suggestions.length - 1;
          }

          const value =
            selectedSuggestionIndex > -1
              ? suggestions[selectedSuggestionIndex]
              : state.inputValue;
          if (selectedSuggestionIndex !== state.selectedSuggestionIndex) {
            setState({
              ...state,
              selectedSuggestionIndex,
              searchValue: value,
            });
          }
        }
      }}
      onLeft={props.onLeft}
      onRight={props.onRight}
      onSpace={props.onSpace}
      disabled={props.disabled}
      toolTip={props.toolTip}
    >
      <>
        {state.searchValue && state.searchValue !== '' && (
          <div
            role="button"
            inputMode="search"
            className={`${theme} close-icon`}
            onMouseDown={(e: any) => {
              e.preventDefault();
              if (props.onClear !== undefined) {
                props.onClear();
              }
              setState({ ...state, searchValue: '' });
            }}
            onKeyDown={(e: any) => {
              if (
                (e.keyCode === 13 || e.keyCode === 32) &&
                state.searchValue &&
                state.searchValue !== ''
              ) {
                if (props.onClear !== undefined) {
                  props.onClear();
                }
                setState({ ...state, searchValue: '' });
              }
            }}
            tabIndex={0}
            aria-label="clear search"
          >
            {' '}
          </div>
        )}
        {state.displaySearchSuggestions &&
          suggestions &&
          suggestions.length > 0 && (
            <SearchSuggestions
              suggestions={suggestions || []}
              selectedSuggestionIndex={state.selectedSuggestionIndex}
              onClick={(e: any) => {
                setState({
                  ...state,
                  searchValue: e.target.selectedValue,
                  displaySearchSuggestions: false,
                });
                if (props.onSearch !== undefined) {
                  props.onSearch(e.target.selectedValue);
                }
              }}
            />
          )}
      </>
    </TextInput>
  );
}
